import * as Yup from 'yup';
import PropTypes from 'prop-types';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TextField from '@mui/material/TextField';
import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';

import isEmpty from 'helpers/isEmpty';
import AmountField from 'core/forms/AmountField';
import Clipboard from 'core/clipboard/Clipboard';
import SubmitButton from 'core/buttons/SubmitButton';

import { zaiMakePayment } from 'app/features/transactions/zaiTransactionSaga';
import { makePaymentReset } from 'app/features/transactions/zaiTransactionSlice';

const schema = Yup.object().shape({
  transactionId: Yup.string().required('Transaction ID is required'),
  webhookId: Yup.string().required('Webhook ID is required'),
  paymentAmount: Yup.number().required('Amount is required').typeError('Enter a refund amount'),
  remarks: Yup.string().required('Remarks is required').typeError('Enter a remarks'),
});

export default function MakePaymentTableRowForm({ row, onMakePaymentSuccess, transactionId }) {
  const dispatch = useDispatch();

  const state = useSelector((state) => state.zaiTransactions.makePayment);

  const { isLoading, webhookId, isSuccess } = state;

  const [formData, setFormData] = useState({
    paymentAmount: null,
    remarks: null,
  });

  const [errors, setErrors] = useState({
    paymentAmount: '',
    remarks: '',
  });

  const clearErrors = (name) => {
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleMakePayment = async () => {
    const requestPayload = {
      ...formData,
      webhookId: row.webhook_id,
      transactionId,
    };

    try {
      await schema.validate(requestPayload, { abortEarly: false });
      dispatch(zaiMakePayment(requestPayload));
    } catch (err) {
      const validationErrors = {};
      err.inner.forEach((error) => {
        if (error.path !== undefined) {
          validationErrors[error.path] = error.errors[0];
        }
      });
      setErrors(validationErrors);
    }
  };

  useEffect(() => {
    if (isSuccess) {
      dispatch(makePaymentReset());
      onMakePaymentSuccess?.();
    }
  }, [isSuccess]);

  return (
    <TableRow>
      <TableCell>{row.sn}</TableCell>
      <TableCell width={300}>
        <Typography>Name: &nbsp; {!isEmpty(row.debtor_name) ? row.debtor_name : 'N/A'} </Typography>
        <Typography fontWeight={500}>Account Details </Typography>
        <Typography>
          BSB: &nbsp; {!isEmpty(row.zai_routing_number) ? row.zai_routing_number : 'N/A'}{' '}
        </Typography>
        <Typography>
          Account Number: &nbsp;
          {!isEmpty(row.debtor_account_no) ? row.debtor_account_no : 'N/A'}
        </Typography>
        <Typography>
          Amount: &nbsp; {row.transaction_currency} &nbsp;
          {!isEmpty(row.transaction_amount) ? row.transaction_amount : 'N/A'}
        </Typography>
      </TableCell>

      <TableCell>
        <Clipboard content={row.webhook_id} />
      </TableCell>

      <TableCell>
        <Clipboard content={row.transaction_id} />
      </TableCell>

      <TableCell align="right">{row.transaction_amount.toLocaleString()}</TableCell>
      <TableCell>{row.transaction_currency}</TableCell>
      <TableCell>
        <AmountField
          type="text"
          name="paymentAmount"
          size="small"
          placeholder="Enter amount"
          value={formData.paymentAmount ?? ''}
          onChange={(e) => {
            setFormData({ ...formData, [e.target.name]: e.target.value });
          }}
          helperText={errors?.paymentAmount}
          error={!!errors?.paymentAmount}
          onFocus={() => clearErrors('paymentAmount')}
        />
      </TableCell>
      <TableCell>
        <TextField
          name="remarks"
          size="small"
          placeholder="Enter remarks"
          value={formData.remarks ?? ''}
          onChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })}
          error={!!errors?.remarks}
          helperText={errors?.remarks}
          onFocus={() => clearErrors('remarks')}
        />
      </TableCell>
      <TableCell>
        <SubmitButton
          size="small"
          onClick={handleMakePayment}
          disabled={isLoading}
          {...(webhookId === row.webhook_id
            ? {
                isLoading,
              }
            : undefined)}
        >
          Submit
        </SubmitButton>
      </TableCell>
    </TableRow>
  );
}

MakePaymentTableRowForm.propTypes = {
  row: PropTypes.any.isRequired,
  onMakePaymentSuccess: PropTypes.func.isRequired,
  transactionId: PropTypes.string.isRequired,
};
